import { formatEntityLabel } from "../lib/format-entity-label";
import type { CourseFocusHandler } from "./course-focus-handler";
import type { AffectedStudent, IssueEvent } from "../types";

interface IssueDetailExpanderProps {
  /** Affected students (name/id + best-effort reason). */
  students?: AffectedStudent[];
  /** Underlying events (teacher's day, student's day). */
  events?: IssueEvent[];
  /** DOM id so a controlling toggle can point `aria-controls` at this region. */
  id?: string;
  /**
   * When supplied, each event's course label becomes a button that jumps to the
   * calendar and highlights the course. Plain text when omitted.
   */
  onCourseFocus?: CourseFocusHandler;
  /**
   * When supplied, each affected-student row shows an "Assign" action that
   * opens the reassign/override modal for that student into any section of the
   * issue's course. Hidden when omitted (e.g. Viewer role or no course).
   */
  onReassignStudent?: (studentIdentifier: string, studentName: string | null) => void;
}

/**
 * Reusable detail region shown when an issue row is expanded. Renders an
 * affected-students list and/or an events list. Shared across the conflict
 * view (affected students) and the teacher-overload / student-no-breaks views
 * (event lists) so those issue types get one consistent drill-down. Renders
 * nothing when both lists are empty.
 */
export function IssueDetailExpander({
  students = [],
  events = [],
  id,
  onCourseFocus,
  onReassignStudent,
}: IssueDetailExpanderProps) {
  if (students.length === 0 && events.length === 0) {
    return null;
  }

  return (
    <div
      id={id}
      role="region"
      data-testid="issue-detail-expander"
      className="mt-2 pt-2 border-t border-border-subtle/50 flex flex-col gap-1.5"
    >
      {students.length > 0 && (
        <ul
          data-testid="issue-detail-students"
          className="flex flex-col gap-1 m-0 p-0 list-none"
        >
          {students.map((s) => (
            <li
              key={s.studentIdentifier}
              data-testid={`issue-detail-student-${s.studentIdentifier}`}
              className="text-[12px] text-text-soft flex flex-wrap items-baseline gap-x-2"
            >
              <span className="font-medium">
                {formatEntityLabel(s.studentName, s.studentIdentifier)}
              </span>
              {/* Truthy (not `!== null`) so an empty-string detail renders no span. */}
              {s.detail && (
                <span className="text-text-muted">{s.detail}</span>
              )}
              {onReassignStudent && (
                <button
                  type="button"
                  onClick={() =>
                    onReassignStudent(s.studentIdentifier, s.studentName)
                  }
                  data-testid={`issue-detail-student-reassign-${s.studentIdentifier}`}
                  // Student-specific accessible name so screen-reader users can
                  // tell the otherwise-identical row buttons apart.
                  aria-label={`Assign ${formatEntityLabel(s.studentName, s.studentIdentifier)} to a section`}
                  className="text-[12px] text-blue-400 hover:text-blue-300 cursor-pointer"
                >
                  Assign
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {events.length > 0 && (
        <ul
          data-testid="issue-detail-events"
          className="flex flex-col gap-1 m-0 p-0 list-none"
        >
          {events.map((e, idx) => (
            // Static, never-reordered list; idx tiebreaks legitimately-duplicate
            // course+section rows (e.g. two meetings of the same section).
            <li
              key={`${e.courseId}::${e.sectionLabel ?? ""}::${idx}`}
              data-testid="issue-detail-event"
              className="text-[12px] text-text-soft flex flex-wrap items-baseline gap-x-2"
            >
              <span className="font-medium">
                {onCourseFocus ? (
                  <button
                    type="button"
                    onClick={() => onCourseFocus(e.courseId, e.sectionLabel)}
                    data-testid="issue-detail-event-course-focus"
                    className="text-blue-400 hover:text-blue-300 underline decoration-dotted cursor-pointer"
                  >
                    {formatEntityLabel(e.courseName, e.courseId)}
                  </button>
                ) : (
                  formatEntityLabel(e.courseName, e.courseId)
                )}
                {e.sectionLabel !== null ? ` · Sec ${e.sectionLabel}` : ""}
              </span>
              <span className="text-text-muted">{e.timeLabel}</span>
              <span className="text-text-faint">({e.durationLabel})</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
